import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useFinancialStats } from '../../hooks/useFinancialStats';
import LoadingSpinner from '../common/LoadingSpinner';
import StatsCard from '../common/StatsCard';
import { formatCurrency } from '../../utils/formatters';

interface Props {
  dateRange: { start_date?: string; end_date?: string };
}

const PAYMENT_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function FinancialPaymentsTab({ dateRange }: Props) {
  const { data: stats, isLoading, error } = useFinancialStats(dateRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Erreur lors du chargement des données</div>;
  if (!stats) return null;

  // Calculs pour les KPIs
  const totalEncaisse = stats.paid_by_month.reduce((sum, item) => sum + item.total, 0);
  const totalFacture = stats.invoiced_by_month.reduce((sum, item) => sum + item.total, 0);
  const tauxRecouvrement = totalFacture > 0 ? (totalEncaisse / totalFacture) * 100 : 0;
  const moyenneEncaissement = totalEncaisse / (stats.paid_by_month.length || 1);

  // Données pour le graphique des modes de paiement
  const paymentMethodsData = stats.payment_methods.map(pm => ({
    name: getPaymentMethodLabel(pm.payment_method),
    value: pm.total,
    count: pm.count
  }));

  // Comparaison facturé vs encaissé
  const comparisonData = stats.invoiced_by_month.map(invoice => {
    const payment = stats.paid_by_month.find(p => p.month === invoice.month);
    return {
      month: new Date(invoice.month).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
      facture: invoice.total,
      encaisse: payment?.total || 0,
      ecart: invoice.total - (payment?.total || 0)
    };
  });

  return (
    <div className="space-y-6">
      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Encaissé"
          value={formatCurrency(totalEncaisse)}
          icon="💳"
          color="green"
        />
        <StatsCard
          title="Taux de Recouvrement"
          value={`${tauxRecouvrement.toFixed(1)}%`}
          icon="📈"
          color={tauxRecouvrement >= 90 ? "green" : tauxRecouvrement >= 75 ? "yellow" : "red"}
        />
        <StatsCard
          title="Moyenne Mensuelle"
          value={formatCurrency(moyenneEncaissement)}
          icon="📊"
          color="blue"
        />
        <StatsCard
          title="Créances Restantes"
          value={formatCurrency(stats.outstanding_amount)}
          icon="⚠️"
          color="red"
        />
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par mode de paiement */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition par Mode de Paiement</h3>
          <PieChart width={500} height={350}>
            <Pie
              data={paymentMethodsData}
              cx={250}
              cy={175}
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {paymentMethodsData.map((entry, index: number) => (
                <Cell key={`cell-${index}`} fill={PAYMENT_COLORS[index % PAYMENT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Montant']} />
          </PieChart>
        </div>

        {/* Évolution des encaissements */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Évolution des Encaissements</h3>
          <LineChart width={500} height={350} data={stats.paid_by_month}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => [formatCurrency(Number(value)), 'Encaissements']} />
            <Legend />
            <Line type="monotone" dataKey="total" stroke="#00C49F" strokeWidth={3} />
          </LineChart>
        </div>
      </div>

      {/* Comparaison Facturé vs Encaissé */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Comparaison Facturé vs Encaissé</h3>
        <BarChart width={1000} height={400} data={comparisonData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis />
          <Tooltip formatter={(value, name) => [
            formatCurrency(Number(value)),
            name === 'facture' ? 'Facturé' : name === 'encaisse' ? 'Encaissé' : 'Écart'
          ]} />
          <Legend />
          <Bar dataKey="facture" fill="#8884d8" name="Facturé" />
          <Bar dataKey="encaisse" fill="#82ca9d" name="Encaissé" />
          <Bar dataKey="ecart" fill="#ff7300" name="Écart" />
        </BarChart>
      </div>

      {/* Détails par mode de paiement */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Analyse Détaillée par Mode de Paiement</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mode de Paiement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre de Transactions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Montant Moyen
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pourcentage
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.payment_methods.map((method, index: number) => (
                <tr key={method.payment_method}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {getPaymentMethodLabel(method.payment_method)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {method.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(method.total)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(method.total / method.count)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {((method.total / totalEncaisse) * 100).toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Fonction utilitaire pour les labels des modes de paiement (adapté pour la Guinée)
function getPaymentMethodLabel(method: string): string {
  const labels: { [key: string]: string } = {
    'cash': 'Espèces',
    'card': 'Carte Bancaire',
    'transfer': 'Virement Bancaire',
    'check': 'Chèque',
    'insurance': 'Assurance Santé',
    'mobile': 'Orange Money/MTN Money',
    'credit': 'Crédit Patient'
  };
  return labels[method] || method;
}